import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  Grid,
  Menu,
  MenuItem,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { WebsocketMessageType } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DropTargetMonitor, useDrop } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { ContentQuickAccess } from "src/components/FlightStatus/cards/ContentQuickAccess";
import { CrewApp } from "src/components/FlightStatus/cards/CrewApp";
import { FlightInfo } from "src/components/FlightStatus/cards/FlightInfo";
import { NoCardFound } from "src/components/FlightStatus/cards/NoCardFound";
import { FlightChip } from "src/components/Flights/FlightChip";
import LoadingWrapper from "src/components/LoadingWrapper/LoadingWrapper";
import { TimeDisplay } from "src/components/TimeDisplay";
import useWebSocket from "src/hooks/useWebsockets";
import { FlightStatusRowRenderer } from "src/pages/FlightStatus/FlightStatusRowRenderer";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  getColumnDefs,
  getFlightData,
  IFlightStatusSort,
  setLoading,
  setUpdating,
  webSocketUpdateFlightStatus,
} from "src/redux/reducers/flightStatusReducer";
import { isDevEnv } from "src/utils";

export interface IContentCellProps {
  flightId: string;
  data?: any;
  useUTC: "local" | "UTC";
  flightFilter: any[];
  setFlightFilter: React.Dispatch<React.SetStateAction<any[]>>;
}

function getValueFromPath(obj: Record<string, any>, path: string): any {
  const keys = path.split(".");
  let current = obj;

  for (const key of keys) {
    if (current && key in current) {
      current = current[key];
    } else {
      return undefined;
    }
  }

  return current;
}

export function FlightStatusWindow(): JSX.Element {
  const columnDefs = useSelector((state: IRootState) => state.flightStatus.columnDefs);
  const sorting = useSelector((state: IRootState) => state.flightStatus.sorting);
  const autoScrollSettings = useSelector((state: IRootState) => state.flightStatus.autoScrollSettings);
  const flightData = useSelector((state: IRootState) => state.flightStatus.flightData);
  const loading = useSelector((state: IRootState) => state.flightStatus.loading);
  const [useUTC, setUseUTC] = useState<"local" | "UTC">("UTC");
  const [sortMethod, setSortMethod] = useState<"ASC" | "DESC">("DESC");
  const [flightStatusSort, setFlightStatusSort] = useState<IFlightStatusSort | null>(null);
  const dispatch = useDispatch();
  const webSocket = useWebSocket();
  const [devAnchorEl, setDevAnchorEl] = useState<null | HTMLElement>(null);
  const devOpen = Boolean(devAnchorEl);
  const [flightFilter, setFlightFilter] = useState<any[]>([]);
  const [filteredFlightIds, setFilteredFlightIds] = useState<string[]>([]);
  const [filteredFlightModalOpen, setFilteredFlightModalOpen] = useState<boolean>(false);
  const [autoScroll, setAutoScroll] = useState<boolean>(false);
  const [autoScrollIdx, setAutoScrollIdx] = useState<number>(0);
  const autoScrollTimerRef = useRef(null);
  const listRef = useRef<List>(null);
  const sizeMap = useRef<{ [key: number]: number }>({});
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const sortOpen = Boolean(sortAnchorEl);

  const [fullScreen, setFullScreen] = useState<boolean>(false);
  const fullScreenRef = useRef<HTMLDivElement>(null);

  const toggleFullScreen = (): void => {
    if (!fullScreen) {
      if (fullScreenRef.current?.requestFullscreen) {
        fullScreenRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = (): void => {
      setFullScreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const getRowSize = useCallback((index: number) => {
    return sizeMap.current[index] || 100;
  }, []);

  const setRowSize = (index: number, size: number): void => {
    if (sizeMap.current[index] !== size) {
      sizeMap.current[index] = size;
      listRef.current?.resetAfterIndex(index);
    }
  };

  const componentMap: { [key: string]: React.FC<any> } = useMemo(() => {
    return {
      FlightInfo: FlightInfo,
      CrewApp: CrewApp,
      ContentQuickAccess: ContentQuickAccess,
      NoCardFound: NoCardFound,
    };
  }, []);

  const devHandleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setDevAnchorEl(event.currentTarget);
  };
  const devHandleClose = (): void => {
    setDevAnchorEl(null);
  };

  const devHandleMenuClick = (type: string): void => {
    if (type === "settings" || type === "both") {
      dispatch(getColumnDefs());
    }
    if (type === "data" || type === "both") {
      dispatch(getFlightData());
    }
  };

  const handleUTCClick = (event: React.MouseEvent<HTMLElement>, newValue: "local" | "UTC"): void => {
    if (newValue) {
      setUseUTC(newValue);
    }
  };
  const handleSortMethodClick = (event: React.MouseEvent<HTMLElement>, newValue: "ASC" | "DESC"): void => {
    if (newValue) {
      setSortMethod(newValue);
    }
  };

  useEffect(() => {
    dispatch(getFlightData()).then(() => {
      dispatch(getColumnDefs()).then(() => {
        dispatch(setLoading(false));
        webSocket.subscribe({ content: { action: "subscribe", subscriptions: { flight: true } } });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof webSocket === "object") {
      if (webSocket?.data?.updateType === WebsocketMessageType.FlightStatusUpdate) {
        dispatch(setUpdating(true));
        dispatch(webSocketUpdateFlightStatus(webSocket.data.status));
        webSocket.clearData();
      }
    }
  }, [dispatch, webSocket]);

  const rows = useMemo(() => {
    if (Object.values(flightData).length > 0) {
      const RowDetails = Object.values(flightData)
        .map((flight: any, idx: number) => {
          if (flight.active) {
            const row: { [key: string]: any } = {
              flightId: flight.flightId,
            };
            if (flight.hasOwnProperty("errorState")) {
              console.log("flight", flight);
              row["errorState"] = flight.errorState;
            }
            Object.keys(columnDefs).forEach((card) => {
              if (flight.cards.hasOwnProperty(card)) {
                row[card] = flight.cards[card].data;
              } else {
                row[card] = {};
              }
            });

            if (Object.keys(row).length >= 1) {
              return row;
            } else {
              return null;
            }
          }
          return null;
        })
        .sort((a: any, b: any) => {
          if (flightStatusSort) {
            const AdataPath = getValueFromPath(a, flightStatusSort.dataPath);
            const BdataPath = getValueFromPath(b, flightStatusSort.dataPath);
            switch (flightStatusSort.type) {
              case "date":
                return moment(AdataPath).isBefore(moment(BdataPath)) ? -1 : 1;
                break;
              case "string":
                return AdataPath.localeCompare(BdataPath);
                break;
              case "number":
                return AdataPath > BdataPath ? -1 : 1;
                break;
              case "count":
                if (Array.isArray(AdataPath) && Array.isArray(BdataPath)) {
                  return AdataPath.length > BdataPath.length ? -1 : 1;
                } else {
                  return 0;
                }
                break;
              case "error": {
                const Aerror = AdataPath ?? {};
                const Berror = BdataPath ?? {};
                return Object.keys(Aerror).length > Object.keys(Berror).length ? -1 : 1;
              }
            }
          }

          return 0;
        });
      if (sortMethod === "DESC") {
        return RowDetails.slice().reverse();
      }
      return RowDetails;
    } else {
      return [];
    }
  }, [flightData, columnDefs, flightStatusSort, sortMethod]);

  useEffect(() => {
    if (sorting) {
      const defaultSort = sorting.find((sortDetail: IFlightStatusSort) => sortDetail.default === true);
      setFlightStatusSort(defaultSort);
    }
  }, [sorting]);

  const jumpToIndex = (index: number): void => {
    if (listRef.current) {
      listRef.current.scrollToItem(index, "start");
    }
  };

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [
      "FLIGHT",
      "SELECTED_FLIGHTS",
      "SELECTED_GROUNDEVENTS",
      "UNASSIGNED_FLIGHT",
      "POSFLIGHT",
      "NONFLIGHT",
      "GROUND_EVENT",
      "FLIGHT_GROUP",
      "UNASSIGNED_FLIGHT",
      "UNASSIGNED_FLIGHTS",
    ],
    drop(dragItem: any, monitor: DropTargetMonitor) {
      const filteredFlights = [...flightFilter];
      const itemArray = dragItem.hasOwnProperty("selectedFlights") ? dragItem.selectedFlights : [dragItem];
      itemArray.forEach((item: any) => {
        const findInFlightsIdx = filteredFlights.findIndex((flight) => {
          if (flight.hasOwnProperty("flight") && item.hasOwnProperty("flight")) {
            return item.flight._id === flight.flight._id;
          }
          if (!flight.hasOwnProperty("flight") && item.hasOwnProperty("flight")) {
            return item.flight._id === flight._id;
          }
          if (flight.hasOwnProperty("flight") && !item.hasOwnProperty("flight")) {
            return item._id === flight.flight._id;
          }
          return item._id === flight._id;
        });
        const findInAllFlightsIdx = flightData.findIndex((flight: any) => {
          if (flight.hasOwnProperty("flight") && item.hasOwnProperty("flight")) {
            return item.flight._id === flight.flightId;
          }
          if (!flight.hasOwnProperty("flight") && item.hasOwnProperty("flight")) {
            return item.flight._id === flight.flightId;
          }
          if (flight.hasOwnProperty("flight") && !item.hasOwnProperty("flight")) {
            return item._id === flight.flight.flightId;
          }
          return item._id === flight._id;
        });
        if (findInFlightsIdx < 0 && findInAllFlightsIdx >= 0) {
          filteredFlights.push(item);
          setFlightFilter(filteredFlights);
        }
      });
    },
    canDrop: (dragItem: any) => {
      const flights = [...flightData];
      const itemArray = dragItem.hasOwnProperty("selectedFlights") ? dragItem.selectedFlights : [dragItem];

      if (itemArray.length === 1) {
        const singleItem = itemArray[0];
        const isInFlightData = flights.some((flight) => {
          if (flight.hasOwnProperty("flight") && singleItem.hasOwnProperty("flight")) {
            return singleItem.flight._id === flight.flightId;
          }
          if (!flight.hasOwnProperty("flight") && singleItem.hasOwnProperty("flight")) {
            return singleItem.flight._id === flight.flightId;
          }
          if (flight.hasOwnProperty("flight") && !singleItem.hasOwnProperty("flight")) {
            return singleItem._id === flight.flight.flightId;
          }
          return singleItem._id === flight._id;
        });

        const findInFlightsIdx = flightFilter.findIndex((flight: any) => {
          if (flight.hasOwnProperty("flight") && singleItem.hasOwnProperty("flight")) {
            return singleItem.flight._id === flight.flight._id;
          }
          if (!flight.hasOwnProperty("flight") && singleItem.hasOwnProperty("flight")) {
            return singleItem.flight._id === flight.flightId;
          }
          if (flight.hasOwnProperty("flight") && !singleItem.hasOwnProperty("flight")) {
            return singleItem._id === flight.flight._id;
          }
          return singleItem._id === flight._id;
        });

        return isInFlightData && findInFlightsIdx < 0;
      }

      if (itemArray.length > 1) {
        return itemArray.some((item: any) =>
          flights.some((flight) => {
            return flights.some((flight) => {
              if (flight.hasOwnProperty("flight") && item.hasOwnProperty("flight")) {
                return item.flight._id === flight.flightId;
              }
              if (!flight.hasOwnProperty("flight") && item.hasOwnProperty("flight")) {
                return item.flight._id === flight.flightId;
              }
              if (flight.hasOwnProperty("flight") && !item.hasOwnProperty("flight")) {
                return item._id === flight.flight.flightId;
              }
              return item._id === flight._id;
            });
          })
        );
      }

      return false;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const handleRemoveFromFilter = (item: any): void => {
    const filteredFlights = [...flightFilter];
    const flightIdx = filteredFlights.findIndex((flight) => {
      if (item.hasOwnProperty("flight") && flight.hasOwnProperty("flight")) {
        return item.flight._id === flight.flight._id;
      }
      if (!item.hasOwnProperty("flight") && flight.hasOwnProperty("flight")) {
        return item._id === flight.flight._id;
      }
      if (item.hasOwnProperty("flight") && !flight.hasOwnProperty("flight")) {
        return item.flight._id === flight._id;
      }
      return item._id === flight._id;
    });

    if (flightIdx >= 0) {
      filteredFlights.splice(flightIdx, 1);
    }
    setFlightFilter(filteredFlights);
  };

  const handleSortButtonClick = (event: any): void => {
    setSortAnchorEl(event.currentTarget);
  };
  const handleSortClose = (): void => {
    setSortAnchorEl(null);
  };

  const dropZoneStyle = {
    position: "relative",
    "&::after": {
      justifyContent: "center",
      alignItems: "center",
      fontSize: "1.4em",
      fontWeight: 900,
      content: canDrop
        ? "'Drop Flight Chips to add to filter. Click \"Apply Filter\" when you are done'"
        : "'This Flight Chip is outside of the Flight Status time window or is already in filter list'",
      backgroundColor: canDrop ? "#013220" : "#660000",
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      opacity: 0.6,
      display: isOver ? "flex" : "none",
    },
  };

  useEffect(() => {
    if (autoScroll && autoScrollSettings.hasOwnProperty("incrementAmount")) {
      let newIndex = autoScrollSettings.hasOwnProperty("incrementAmount")
        ? autoScrollIdx + autoScrollSettings.incrementAmount
        : autoScrollIdx + 10;
      if (newIndex >= flightData.length) {
        newIndex = 0;
      }
      autoScrollTimerRef.current = setTimeout(() => {
        setAutoScrollIdx(newIndex);
        jumpToIndex(newIndex);
      }, autoScrollSettings.interval ?? 5000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoScroll, autoScrollSettings, autoScrollIdx]);

  return (
    <Box ref={fullScreenRef} sx={{ width: "100%", height: fullScreen ? "100svh" : "calc(100svh - 108px)" }}>
      <Paper
        sx={{ width: "100%", height: fullScreen ? "100svh" : "calc(100svh - 108px)", pl: 2, ...dropZoneStyle }}
        ref={drop}
      >
        <Grid container spacing={0}>
          <Grid
            item
            xs={"auto"}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              py: 1,
              borderBottom: `1px solid ${theme.palette.divider}`,
              fontSize: "0.9em !important",
            })}
          >
            <Box
              sx={(theme) => ({
                fontSize: "1.4em",
                fontWeight: 900,
                backgroundColor: theme.palette.grey[800],
                p: 0.2,
                px: 1,
                border: ` 1px solid ${theme.palette.grey[600]}`,
                color: theme.palette.warning.main,
                borderRadius: "4px",
              })}
            >
              <TimeDisplay allowTimezoneControl={false} timeFormat={"HH:mm:ss"} /> UTC
            </Box>
            {sorting && (
              <>
                <Button
                  id={"sort-menu-flight-status-button"}
                  aria-controls={sortOpen ? "sort-menu-flight-status" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleSortButtonClick}
                  size="small"
                  variant="contained"
                  color="info"
                  sx={{ p: 0, px: 1, ml: 2, fontSize: "0.8em !important" }}
                >
                  {flightStatusSort ? `${flightStatusSort.label}` : "Sort ..."}
                </Button>
                <Menu
                  id={"sort-menu-flight-status"}
                  anchorEl={sortAnchorEl}
                  open={sortOpen}
                  onClose={handleSortClose}
                  MenuListProps={{
                    "aria-labelledby": "sort-menu-flight-status-button",
                  }}
                >
                  {sorting &&
                    sorting.map((sortDetail: IFlightStatusSort, key: number) => {
                      return (
                        <MenuItem
                          key={key}
                          sx={{ fontSize: "0.8em !important" }}
                          onClick={() => {
                            setFlightStatusSort(sortDetail);
                          }}
                        >
                          {sortDetail.label}
                        </MenuItem>
                      );
                    })}
                </Menu>
                <ToggleButtonGroup
                  value={sortMethod}
                  exclusive
                  sx={{ mx: 2, "& .MuiToggleButton-root": { py: 0 } }}
                  onChange={handleSortMethodClick}
                  aria-label="Sort Method"
                  size="small"
                >
                  <ToggleButton value={"ASC"} aria-label="ASC" sx={{ fontSize: "0.8em !important" }}>
                    ASC
                  </ToggleButton>
                  <ToggleButton value={"DESC"} aria-label="DESC" sx={{ fontSize: "0.8em !important" }}>
                    DESC
                  </ToggleButton>
                </ToggleButtonGroup>
              </>
            )}
            <ToggleButtonGroup
              value={useUTC}
              exclusive
              sx={{ "& .MuiToggleButton-root": { py: 0 } }}
              onChange={handleUTCClick}
              aria-label="TimeZone to use"
              size="small"
            >
              <ToggleButton value={"local"} aria-label="local" sx={{ fontSize: "0.8em !important" }}>
                Local
              </ToggleButton>
              <ToggleButton value={"UTC"} aria-label="UTC" sx={{ fontSize: "0.8em !important" }}>
                UTC
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid
            item
            xs
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              ml: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Grid container spacing={0}>
              <Grid item xs="auto">
                <ButtonGroup variant="contained" size="small" color="info" sx={{ mt: "3px" }}>
                  <Button
                    sx={{ py: 0, fontSize: "0.8em !important" }}
                    onClick={() => {
                      setFilteredFlightModalOpen(true);
                    }}
                    disabled={flightFilter.length === 0}
                  >
                    Flight Filter ({flightFilter.length})
                  </Button>
                  <Button
                    sx={{ py: 0, fontSize: "0.8em !important" }}
                    onClick={() => {
                      setFlightFilter([]);
                      setFilteredFlightIds([]);
                    }}
                  >
                    Clear Filter
                  </Button>
                  <Button
                    sx={{ py: 0, fontSize: "0.8em !important" }}
                    onClick={() => {
                      const flightIds = flightFilter.map((flight: any) => {
                        if (flight.hasOwnProperty("flight")) {
                          return flight.flight._id;
                        }
                        return flight._id;
                      });
                      setFilteredFlightIds(flightIds);
                    }}
                  >
                    Apply Filter
                  </Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            xs={"auto"}
            sx={(theme) => ({
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              mr: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <ButtonGroup variant="contained" color="info" size="small">
              <Button sx={{ p: 0, px: 1, fontSize: "0.8em !important" }} onClick={toggleFullScreen}>
                FullScreen
              </Button>
              <Button
                onClick={() => {
                  if (autoScroll === true) {
                    setAutoScrollIdx(0);
                    jumpToIndex(0);
                    clearTimeout(autoScrollTimerRef.current);
                  }
                  setAutoScroll(!autoScroll);
                }}
                sx={{ p: 0, px: 1, fontSize: "0.8em !important" }}
                color={autoScroll ? "success" : "info"}
              >
                Autoscroll: {autoScroll ? "On" : "OFF"}
              </Button>
              {isDevEnv() && (
                <>
                  <Button
                    id="dev-button"
                    color={"warning"} //change this to the dev color when merged back to rc
                    size="small"
                    variant="contained"
                    aria-controls={devOpen ? "dev-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={devOpen ? "true" : undefined}
                    onClick={devHandleClick}
                    sx={{ py: 0, fontSize: "0.8em !important" }}
                  >
                    Dev Menu
                  </Button>
                  <Menu
                    id="basic-menu"
                    anchorEl={devAnchorEl}
                    open={devOpen}
                    onClose={devHandleClose}
                    MenuListProps={{
                      "aria-labelledby": "dev-button",
                    }}
                  >
                    <MenuItem
                      sx={{ fontSize: "0.8em !important" }}
                      onClick={() => {
                        devHandleClose();
                        devHandleMenuClick("settings");
                      }}
                    >
                      Refresh Settings
                    </MenuItem>
                    <MenuItem
                      sx={{ fontSize: "0.8em !important" }}
                      onClick={() => {
                        devHandleClose();
                        devHandleMenuClick("data");
                      }}
                    >
                      Refresh Data
                    </MenuItem>
                    <MenuItem
                      sx={{ fontSize: "0.8em !important" }}
                      onClick={() => {
                        devHandleClose();
                        devHandleMenuClick("both");
                      }}
                    >
                      Refresh Everything
                    </MenuItem>
                  </Menu>
                </>
              )}
            </ButtonGroup>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              height: fullScreen ? "calc(100svh - 60px)" : "calc(100svh - 176px)",
              mt: 0,
              overflowY: "scroll",
              textOverflow: "hidden",
            }}
          >
            {loading && (
              <Box sx={{ mt: "25%" }}>
                <LoadingWrapper message="Loading Flight Table Defintions & Data" />
              </Box>
            )}
            {rows && !loading && (
              <AutoSizer>
                {({ height, width }: { height: number; width: number }) => {
                  return (
                    <List
                      ref={listRef}
                      height={height}
                      itemCount={filteredFlightIds.length > 0 ? filteredFlightIds.length : rows.length}
                      estimatedItemSize={100}
                      width={width - 10}
                      itemSize={getRowSize}
                      itemData={{
                        rows,
                        columnDefs,
                        componentMap,
                        useUTC,
                        flightFilter,
                        setFlightFilter,
                        filteredFlightIds,
                        sizeMap,
                        setRowSize,
                      }}
                    >
                      {FlightStatusRowRenderer}
                    </List>
                  );
                }}
              </AutoSizer>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={filteredFlightModalOpen}
        onClose={() => {
          setFilteredFlightModalOpen(false);
        }}
      >
        <DialogContent sx={(theme) => ({ backgroundColor: theme.palette.background.paper })}>
          <Typography component={"h2"}>Flights In Current Filter</Typography>
          <Alert severity="info" variant="filled">
            Double clicking a flight chip will remove it from the filter. You can also double click a flight chip in the
            main window to re-add it.
          </Alert>
          <Box sx={{ py: 2, width: "100%" }}>
            {flightFilter &&
              flightFilter.map((flight: any, key: number) => {
                const flightChipData = flight.hasOwnProperty("flight") ? flight.flight : flight;
                return (
                  <Box
                    key={key}
                    sx={{ display: "inline-flex" }}
                    onDoubleClick={() => {
                      handleRemoveFromFilter(flight);
                    }}
                  >
                    <FlightChip showFlightNumberOnly={true} data={flightChipData} disableContext={true} />
                  </Box>
                );
              })}
          </Box>
          <Box sx={{ width: "!00%", textAlign: "right" }}>
            <ButtonGroup variant="contained" size="small">
              <Button
                sx={{ py: 0 }}
                disabled={flightFilter.length === filteredFlightIds.length}
                onClick={() => {
                  const flightIds = flightFilter.map((flight: any) => {
                    if (flight.hasOwnProperty("flight")) {
                      return flight.flight._id;
                    }
                    return flight._id;
                  });
                  setFilteredFlightIds(flightIds);
                }}
              >
                Apply Filter & Close
              </Button>
              <Button
                onClick={() => {
                  setFilteredFlightModalOpen(false);
                }}
                color="error"
              >
                Close
              </Button>
            </ButtonGroup>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
